import type { ScenarioSpec } from "./types";

/**
 * Ransomware Attack — "Andrea at the bank".
 *
 * A premium, multi-scene course that follows one mistake down the whole kill
 * chain, CUTTING between two procedural 3D environments (open-plan bank office
 * <-> hacker room):
 *   1. Andrea's desk phone rings — "Ben from IT" needs an urgent security update
 *      run now. Hanging up to verify is the safe choice and scores; the course
 *      then replays the rest as "what would have happened" (forced beats score
 *      no risk), so every learner still sees the consequences.
 *   2. Zoom to the PC: a lure email from the "IT Service Desk" carries
 *      update.exe. Andrea saves it.
 *   3. File Explorer: she double-clicks update.exe (UAC confirm) and runs it.
 *   4. The screen floods red — a ransomware note with a ticking countdown.
 *   5. Cut to the hacker room: a dark-web leak blog publishes the double-
 *      extortion post (pay or the bank's client data is leaked).
 *   6. Cut back to the office TV: a BREAKING-NEWS bulletin — read by an AI
 *      DEEPFAKE anchor (avatar provider) — reports the breach as the bank's
 *      share price collapses.
 *   7. Later that week, back at the rebuilt PC: Edge -> short ransomware film
 *      -> quiz (same pattern as phishing-office).
 *
 * Assets (self-hosted /cdn/sim/ransomware/*): generated by
 * ops/render-ransomware-assets.mjs (anchor portrait + HeyGen news clip). The
 * IT-call + hacker voices are pre-rendered clips (ops/prerender-voice.mjs).
 */

const CDN = "/cdn/sim/ransomware";
const ANCHOR = `${CDN}/img/anchor.png`;

// The news bulletin script MUST match the rendered anchor clip audio
// (render-ransomware-assets.mjs). Kept ~25s when read aloud.
const NEWS_SCRIPT =
  "Good evening. We begin with breaking news in the financial sector. Northbank, one of the country's largest retail banks, has been crippled by a major ransomware attack. Internal systems are down, branches have closed, and the attackers are threatening to publish the personal data of millions of customers unless a ransom is paid within forty-eight hours. Northbank shares have fallen sharply on the news, wiping hundreds of millions from the bank's value. Regulators have opened an investigation, and customers are being urged to remain alert for fraud.";

// The anchor's "bridge" to camera AFTER the bulletin and BEFORE the data slides,
// so the broadcast flows on without a black-TV cut. MUST match bridge.mp4
// (render-ransomware-assets.mjs). Kept ~12s when read aloud.
const BRIDGE_SCRIPT =
  "Now, before we move on, I want to put this in context for everyone watching \u2014 because what happened to Northbank today is not a one-off. It is part of a fast-growing global threat, and the numbers are sobering. Let me show you.";

export const ransomwareOfficeScenario: ScenarioSpec = {
  version: 1,
  id: "ransomware-office",
  title: "Ransomware Attack at the Bank",
  summary: {
    goal: "See how one unverified IT call can lead to ransomware and stolen data, and learn to verify IT requests and refuse updates that arrive by email.",
    youDo: "Take a call from the IT service desk, deal with the email that follows on your PC, follow what happens next all the way to the evening news, then watch a short film on ransomware.",
    duration: "10-13 min",
    tags: ["ransomware", "voice_phishing", "malware", "verification", "email_phishing"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Ransomware — one fake IT call ends in an encrypted bank and a deepfake on the news",
  language: "en",
  ambientUrl: "/audio/office-ambience.mp3",
  ambientVolume: 0.25,
  music: "office",

  scene: {
    environment: { splat: {}, procedural: "office", backdropColor: "#d8e0e8" },
    environments: [
      {
        // base office, re-registered so beats can CUT back to it from the hacker room.
        id: "office",
        environment: { splat: {}, procedural: "office", backdropColor: "#d8e0e8" },
        ambientUrl: "/audio/office-ambience.mp3",
        ambientVolume: 0.25,
        music: "office",
      },
      {
        id: "hacker",
        environment: { splat: {}, procedural: "hacker_room", backdropColor: "#0a0b0e" },
        ambientUrl: "",
        music: "dark",
      },
    ],
    props: [],
    waypoints: [
      // --- office ---
      { id: "wp_office_establish", position: [2.6, 1.9, 2.8], target: [0.0, 1.0, -0.3], fov: 50, durationS: 2.2, ease: "easeInOut" },
      { id: "wp_office_recut", position: [2.6, 1.9, 2.8], target: [0.0, 1.0, -0.3], fov: 50, durationS: 0 },
      { id: "wp_desk", position: [0.35, 1.12, 0.98], target: [0.5, 0.82, -0.05], fov: 44, durationS: 2.4, ease: "easeInOut" },
      { id: "wp_screen", position: [0.0, 0.95, 0.52], target: [0.0, 0.95, -0.33], fov: 36, durationS: 2.4, ease: "easeInOut" },
      { id: "wp_tv", position: [-2.5, 2.0, 1.25], target: [-2.5, 2.12, -2.0], fov: 46, durationS: 2.6, ease: "easeInOut" },
      // --- hacker room (hard cut in, then push to laptop) ---
      { id: "wp_hacker_establish", position: [0.0, 1.6, 2.6], target: [0.0, 1.0, -0.3], fov: 52, durationS: 0 },
      { id: "wp_hacker_laptop", position: [0.0, 1.12, 0.95], target: [0.0, 0.95, -0.2], fov: 38, durationS: 2.6, ease: "easeInOut" },
    ],
    initialWaypointId: "wp_office_establish",
  },

  devices: [
    {
      id: "itcall",
      kind: "itcall",
      config: {
        app: "Desk phone",
        clock: "10:40",
        callerName: "Ben \u2014 IT Service Desk",
        callerSubtitle: "Internal \u00B7 ext. 4471",
        voiceId: "ash",
        language: "en-US",
        greeting:
          "Hi Andrea, this is Ben from the IT Service Desk. Sorry to call out of the blue \u2014 we've detected a serious security vulnerability on your machine and I need you to run an urgent patch right now before it spreads.",
        rounds: [
          {
            prompt: "He wants you to install a security update immediately. What do you say?",
            options: [
              {
                id: "manager",
                label: "This is sudden \u2014 can I check with my manager first?",
                category: "verify",
                reply:
                  "I completely understand. Your manager's actually already signed off on the emergency patch rollout this morning \u2014 you're one of the last machines left, and it's flagged critical, so I really can't let it wait.",
              },
              {
                id: "hangup_1",
                label: "I'll hang up and call the Service Desk back on the intranet number.",
                category: "verify",
                hangup: true,
              },
              {
                id: "whoareyou",
                label: "How do I know you're really from IT?",
                category: "question",
                reply:
                  "Good instinct \u2014 it's ticket INC-44871, you can look it up after. I'm Ben Carter, second-line support, I sit on the third floor next to the server room. Now, we just need to move quickly here.",
              },
              {
                id: "agree_early",
                label: "Okay, no problem \u2014 what do I need to do?",
                comply: true,
              },
            ],
          },
          {
            prompt: "He has an answer for everything. Now what?",
            options: [
              {
                id: "hangup_2",
                label: "I'm ending this call and ringing IT myself to check.",
                category: "verify",
                hangup: true,
              },
              {
                id: "tomorrow",
                label: "Can this wait until tomorrow? I'm busy.",
                category: "refuse",
                reply:
                  "I wish it could, but this exploit is being actively used right now \u2014 if we don't patch in the next few minutes your files could be at risk, and that's on both of us. It really is two minutes, I promise.",
              },
              {
                id: "email",
                label: "Can you just email me the instructions instead?",
                category: "question",
                reply:
                  "That's exactly what I'm about to do \u2014 I'll send the patch straight to your inbox so you have it in writing. I just need you to run it the moment it arrives so we're covered.",
              },
              {
                id: "agree_mid",
                label: "Alright, fine \u2014 just walk me through it.",
                comply: true,
              },
            ],
          },
          {
            prompt: "He's not taking no for an answer. The pressure is relentless.",
            options: [
              {
                id: "comply",
                label: "Okay\u2026 fine. What do I need to do?",
                comply: true,
              },
              {
                id: "hangup_3",
                label: "No. I'm hanging up and calling IT on the official number.",
                category: "refuse",
                hangup: true,
              },
            ],
          },
        ],
        closing:
          "Perfect, thank you Andrea. I'm pushing the patch to your email right now \u2014 you'll see it from the IT Service Desk in under a minute. Just open it, save the attachment and run it. You're all set, have a good one.",
      },
    },
    {
      id: "desktop",
      kind: "desktop",
      config: {
        clock: "10:42 AM",
        date: "Tue 23 Jun",
        icons: [
          { id: "mail", label: "Outlook", icon: "mail", opens: "mail" },
          { id: "files", label: "This PC", icon: "folder", opens: "files" },
          { id: "chrome", label: "Edge", icon: "chrome", opens: "browser" },
        ],
      },
    },
    {
      id: "rwbrief",
      kind: "browser",
      config: {
        homeUrl: "learn.northbank.example/ransomware",
        targetUrl: "learn.northbank.example/ransomware",
        loadsPage: "blank",
        awarenessVideo: {
          url: "/cdn/sim/awareness-videos/ransomware-role/ransomware-role.mp4",
          brand: "Security awareness",
          headline: "Ransomware: your role",
          body:
            "One call and one file were enough to stop Northbank. This short film shows how ransomware gets in, and what you can do to stop it.",
          playLabel: "Watch the video",
          continueLabel: "Continue to quiz",
        },
      },
    },
    {
      id: "mail",
      kind: "mail",
      config: {
        appTitle: "Outlook",
        emails: [
          {
            id: "e_team",
            from: "Sofia Marin",
            fromAddress: "sofia.marin@northbank.com",
            subject: "Re: Q3 reconciliation",
            preview: "Thanks Andrea \u2014 numbers look right, approving now.",
            body: "Thanks Andrea \u2014 the numbers look right to me, approving now.\n\nSofia",
            receivedAt: "09:58",
          },
          {
            id: "e_lure",
            from: "IT Service Desk",
            fromAddress: "it-servicedesk@northbank-it-support.com",
            subject: "URGENT: Security patch \u2014 run immediately (ref INC-44871)",
            preview: "As discussed on the phone, please run the attached patch now.",
            to: "andrea.rossi@northbank.com",
            receivedAt: "10:41",
            unread: true,
            lure: true,
            body:
              "Hi Andrea,\n\nAs discussed on the phone, your workstation is affected by a critical security vulnerability. Please SAVE and RUN the attached patch immediately to protect your files.\n\nThis is time-sensitive \u2014 our records show your machine is still unpatched.\n\nThank you for acting quickly.\n\nBen Carter\nIT Service Desk \u00B7 ext. 4471\nNorthbank",
            attachments: [
              { id: "att_exe", name: "Security_Update_INC-44871.exe", type: "exe", size: "2.4 MB", danger: true },
            ],
          },
        ],
      },
    },
    {
      id: "files",
      kind: "fileexplorer",
      config: {
        windowTitle: "File Explorer",
        path: "This PC > Downloads",
        runFileId: "f_exe",
        runConfirm: {
          title: "User Account Control",
          body: "Do you want to allow this app from an unknown publisher to make changes to your device?\n\nSecurity_Update_INC-44871.exe\nPublisher: Unknown",
          runLabel: "Yes, run it",
          cancelLabel: "No",
        },
        files: [
          { id: "f_exe", name: "Security_Update_INC-44871.exe", type: "exe", size: "2.4 MB", date: "Today 10:41", danger: true },
          { id: "f_pdf", name: "Q3_reconciliation.pdf", type: "pdf", size: "880 KB", date: "Today 09:30" },
          { id: "f_xls", name: "branch_targets.xlsx", type: "doc", size: "1.1 MB", date: "Yesterday" },
          { id: "f_img", name: "team_offsite.jpg", type: "img", size: "3.2 MB", date: "Mon" },
          { id: "f_zip", name: "policies_2026.zip", type: "zip", size: "5.6 MB", date: "Mon" },
        ],
      },
    },
    {
      id: "ransom",
      kind: "ransomnote",
      config: {
        title: "YOUR FILES HAVE BEEN ENCRYPTED",
        org: "NORTHBANK",
        body:
          "Every file on this network has been encrypted with military-grade encryption. Your backups have been deleted. The only way to recover your data is to pay for the decryption key. Do not turn off your computer. Do not contact the police \u2014 we will know.",
        amount: "0.85 BTC (~ \u20AC58,000)",
        walletLabel: "Send payment to this wallet:",
        wallet: "bc1q9x4n2r7k0m3h8s5v6t1y2u3w4z7a8b9c0d1e2",
        deadlineSeconds: 48 * 3600,
        fileList: [
          "Q3_financials.xlsx", "client_database.bak", "payroll_2026.xlsx",
          "board_minutes.docx", "loan_applications.pdf", "kyc_scans",
          "mortgage_records.csv", "card_transactions.db",
        ],
        continueLabel: "What just happened?",
        onScreen: true,
        processName: "Security_Update_INC-44871.exe",
        clock: "10:43 AM",
      },
    },
    {
      id: "leak",
      kind: "leaksite",
      config: {
        siteName: "ShadowLeak",
        victim: "Northbank",
        newVictimToast: "New victim online: Northbank \u2014 encryption confirmed",
        line:
          "Northbank's locked up tight \u2014 and I copied everything before I encrypted it. Let's put them on the wall and start the clock. They'll pay twice.",
        voiceId: "ash",
        language: "en-US",
        steps: [
          "Packaging exfiltrated archive (412 GB)",
          "Uploading to mirror nodes",
          "Generating victim entry",
          "Publishing extortion post",
        ],
        post: {
          headline: "NORTHBANK \u2014 48 HOURS TO PAY",
          body:
            "Northbank has been fully encrypted. We have exfiltrated 412 GB of internal data including customer KYC documents, account records and payroll. Pay within 48 hours or the complete dataset will be published and sold.",
          deadline: "Auto-publish in 48:00:00",
          sample: [
            "northbank_client_database_sample.csv",
            "kyc_scans_batch_01.zip",
            "board_minutes_2026Q2.pdf",
          ],
        },
        continueLabel: "Why this is so damaging",
      },
    },
    {
      id: "news",
      kind: "newscast",
      config: {
        channel: "NBC24",
        anchorName: "Sarah Lane \u00B7 Finance Correspondent",
        lowerThird: "NORTHBANK HIT BY MAJOR RANSOMWARE ATTACK",
        ticker:
          "Northbank systems offline \u00B7 branches closed nationwide \u00B7 attackers demand ransom within 48 hours \u00B7 regulator opens investigation \u00B7 customers warned of fraud \u00B7 ",
        clipUrl: `${CDN}/clips/newscast.mp4`,
        posterUrl: ANCHOR,
        script: NEWS_SCRIPT,
        voiceId: "coral",
        language: "en-US",
        stock: {
          symbol: "NBK",
          change: "\u25BC 38.2%",
          points: [100, 99, 98, 95, 90, 82, 73, 66, 61, 58, 55, 52],
        },
        continueLabel: "Learn more about the threat",
        skipLabel: "Skip the news",
      },
    },
    {
      id: "news2",
      kind: "newsexplainer",
      config: {
        channel: "NBC24",
        anchorName: "Sarah Lane \u00B7 Finance Correspondent",
        posterUrl: ANCHOR,
        voiceId: "coral",
        language: "en-US",
        lowerThird: "RANSOMWARE \u2014 THE BIGGER PICTURE",
        // anchor speaks straight on from the bulletin (rendered clip if present,
        // else portrait + TTS), then the view zooms in and the slides begin.
        bridgeClipUrl: `${CDN}/clips/bridge.mp4`,
        bridgeText: BRIDGE_SCRIPT,
        segments: [
          {
            kicker: "The cost",
            title: "Ransomware is now one of the fastest-growing crimes on earth.",
            narration:
              "And the scale of this is staggering. Ransomware is now one of the fastest-growing crimes on earth. In twenty fifteen it cost the world around three hundred million dollars. This year, that figure is over fifty-seven billion \u2014 and analysts expect it to pass two hundred and seventy-five billion dollars a year by twenty thirty-one.",
            chart: {
              type: "bars",
              caption: "Estimated total global ransomware damage per year (downtime, recovery, fines, lost trust).",
              source: "Source: Cybersecurity Ventures, 2025",
              bars: [
                { label: "2015", value: 0.325, display: "$325M" },
                { label: "2021", value: 20, display: "$20B" },
                { label: "2025", value: 57, display: "$57B", hot: true },
                { label: "2028", value: 125, display: "$125B" },
                { label: "2031", value: 275, display: "$275B", hot: true },
              ],
            },
          },
          {
            kicker: "Who gets hit",
            title: "No industry is safe \u2014 and finance is squarely in the crosshairs.",
            narration:
              "And no industry is safe. Manufacturing and healthcare are hit hardest, but finance, retail, construction and the public sector are all squarely in the crosshairs. If an organisation holds valuable data or simply can't afford downtime, it is a target.",
            chart: {
              type: "hbars",
              caption: "Approximate share of ransomware attacks by sector.",
              source: "Source: Zscaler ThreatLabz & industry reporting, 2024",
              bars: [
                { label: "Manufacturing", value: 25, display: "~25%" },
                { label: "Healthcare", value: 18, display: "~18%" },
                { label: "Business services", value: 15, display: "~15%" },
                { label: "Retail", value: 12, display: "~12%" },
                { label: "Finance & insurance", value: 9, display: "~9%", hot: true },
                { label: "Construction", value: 8, display: "~8%" },
              ],
            },
          },
          {
            kicker: "How it starts",
            title: "Almost every attack begins with one person.",
            narration:
              "But here is what nearly all of these attacks have in common: they start with a person. More than two-thirds of breaches involve human error or manipulation \u2014 a convincing phone call, a fake email, a single click. In fact, the median time for someone to fall for a phishing email is under sixty seconds.",
            chart: {
              type: "stat",
              big: "68%",
              sub: "of breaches involve a human element \u2014 not a hacker breaking in, but a person being deceived.",
              source: "Source: Verizon Data Breach Investigations Report, 2024",
              bullets: [
                "A convincing call or email impersonating IT, a colleague or a supplier",
                "A single click \u2014 median time to fall for a phishing email: under 60 seconds",
                "Reused or stolen passwords handed over to the wrong person",
              ],
            },
          },
          {
            kicker: "The takeaway",
            title: "The strongest defence isn't a firewall. It's you.",
            narration:
              "Which is why the most important defence is not a firewall \u2014 it is you. A moment of doubt, a quick call back to verify, a refusal to run an unexpected file. That is what stops an attack like the one that brought Northbank to its knees today. Back to you in the studio.",
            chart: {
              type: "stat",
              big: "< 60s",
              sub: "is all it takes to fall for a phishing email \u2014 so the antidote is simply to slow down.",
              source: "Your verification protects the whole organisation.",
              bullets: [
                "Pause and verify unexpected requests on a number you look up yourself",
                "Never run a file or 'update' that arrives by email \u2014 even if a call 'confirmed' it",
                "Report anything suspicious early \u2014 fast reporting limits the damage",
              ],
            },
          },
        ],
        continueLabel: "Continue",
        skipLabel: "Skip the news",
      },
    },
  ],

  voiceAttackers: {
    main: {
      persona: {
        name: "Ransomware crew (IT-support impersonation)",
        role: "Initial-access operator posing as internal IT, plus the extortion crew",
        pretext: "it_support",
        language: "en",
        voiceId: "ash",
      },
      goal:
        "Talk an employee into running malware via a fake 'urgent IT update' call + email, then encrypt and extort the bank.",
      opening: "Hi Andrea, this is Ben from IT…",
      allowedTopics: ["the (fake) urgent security update", "the (fake) IT ticket"],
      scoringEvents: [
        { id: "trusted_unverified_it", label: "Trusted an unverified 'IT' caller and agreed to run a patch", kind: "risk", weight: 60 },
        { id: "saved_malicious_attachment", label: "Saved an executable attachment from an unverified email", kind: "risk", weight: 15 },
        { id: "executed_attachment", label: "Ran update.exe \u2014 detonating the ransomware", kind: "risk", weight: 25 },
        { id: "verified_it_caller", label: "Hung up and verified IT on the official number", kind: "positive", weight: 60 },
        { id: "reported_lure_email", label: "Reported the 'patch' email instead of saving the attachment", kind: "positive", weight: 15 },
        { id: "refused_to_run_exe", label: "Did not run update.exe and reported it", kind: "positive", weight: 25 },
      ],
      forbidden: [],
      maxDurationS: 0,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Ransomware Attack\nat the Bank",
      kicker: "Mission 01 \u00b7 Incident",
      highlight: "Northbank",
      guide: {
        title: "Start",
        body: "You're Andrea, a clerk on the busy floor of Northbank. It's a normal Tuesday morning with reports to finish and emails to answer when your desk phone rings.",
      },
    },

    { id: "b_office_establish", type: "camera", waypointId: "wp_office_establish" },
    { id: "b_to_desk", type: "camera", waypointId: "wp_desk" },
    {
      id: "b_itcall",
      type: "device",
      deviceId: "itcall",
      guide: {
        title: "Your phone is ringing",
        body: "It's the IT Service Desk calling. Click Answer.",
        actionLabel: "Answer",
      },
    },
    {
      id: "b_teach_it",
      type: "teach",
      title: "That call should have been verified",
      teach: {
        body:
          "An unsolicited call claiming to be IT, demanding you run something **right now**, is a classic pretext. A real ticket number, a name and a confident manner cost the attacker nothing \u2014 and the urgency is engineered to stop you checking. The safe move is simple: hang up and call IT back on the **official internal number**, not one the caller gave you.",
        spot: [
          "Unsolicited 'IT' call with manufactured urgency ('right now', 'actively exploited').",
          "Every objection is smoothly overruled with a ready-made excuse.",
          "A ticket number / name sounds like proof but can be invented on the spot.",
        ],
        protect: [
          "Hang up and call IT back on the number from your intranet, not the caller.",
          "Real IT will never mind you verifying \u2014 a scammer will pressure you not to.",
          "Never run software because a phone call told you to.",
        ],
        remember: "Verify unexpected 'IT' on a number you look up yourself \u2014 always.",
      },
    },
    {
      id: "b_teach_it_safe",
      type: "teach",
      title: "You hung up and checked",
      teach: {
        outcome: "safe",
        body:
          "You called the Service Desk back on the **official internal number**. Nobody called 'Ben Carter' works there and ticket INC-44871 does not exist. An unsolicited call claiming to be IT, demanding you run something **right now**, is a classic pretext, and hanging up to verify is exactly what defeats it.",
        spot: [
          "Unsolicited 'IT' call with manufactured urgency ('right now', 'actively exploited').",
          "Every objection is smoothly overruled with a ready-made excuse.",
          "A ticket number / name sounds like proof but can be invented on the spot.",
        ],
        protect: [
          "Hang up and call IT back on the number from your intranet, not the caller.",
          "Real IT will never mind you verifying \u2014 a scammer will pressure you not to.",
          "Never run software because a phone call told you to.",
        ],
        remember: "Verify unexpected 'IT' on a number you look up yourself \u2014 always.",
        continueLabel: "See what would have happened",
        whatIf: "If you had agreed, Ben's 'patch' would now be in your inbox.",
      },
    },

    { id: "b_to_screen", type: "camera", waypointId: "wp_screen" },
    {
      id: "b_desktop",
      type: "device",
      deviceId: "desktop",
      guide: { title: "Your PC", body: "Ben said an email is coming. Open Outlook to check.", actionLabel: "Outlook" },
      completeOn: { action: "open_app", arg: "mail" },
    },
    {
      id: "b_mail",
      type: "device",
      deviceId: "mail",
      forced: true,
      guide: {
        title: "The email arrived",
        body: "Click the email from IT Service Desk to open it. Then decide: Save the attached patch, or report the email.",
        actionLabels: ["IT Service Desk", "Save"],
      },
      safeChoice: { label: "Don't save it: report the email", eventId: "reported_lure_email" },
      completeOn: { action: "save_attachment", arg: "e_lure" },
    },
    {
      id: "b_teach_mail_safe",
      type: "teach",
      title: "You didn't take the bait",
      teach: {
        outcome: "safe",
        body:
          "The 'patch' was an **.exe attached to an email**, sent right after an unexpected call. Real updates arrive through managed IT tools, never as attachments. Reporting it instead of saving it stops the attack before anything lands on the PC.",
        spot: [
          "An '.exe' attachment from email is almost never a legitimate update.",
          "The email arrived exactly as the caller promised: the call set it up.",
          "The sender name says 'IT Service Desk', which anyone can type.",
        ],
        protect: [
          "Never save or open executables sent by email.",
          "Report the email to security, and mention the call that came with it.",
          "If an update is really needed, IT installs it through its own tools.",
        ],
        remember: "Updates don't arrive as email attachments.",
        continueLabel: "See what would have happened",
        whatIf: "If you had saved the patch, it would now be waiting in Downloads.",
      },
    },
    {
      id: "b_files",
      type: "device",
      deviceId: "files",
      forced: true,
      guide: {
        title: "Run the update",
        body: "The patch is in Downloads. Click Security_Update_INC-44871.exe, then decide: Yes, run it, or don't run it.",
        actionLabels: ["Security_Update_INC-44871.exe", "Yes, run it"],
      },
      safeChoice: { label: "Don't run it: delete it and call IT", eventId: "refused_to_run_exe" },
      completeOn: { action: "run_file", arg: "f_exe" },
    },
    {
      id: "b_teach_run_safe",
      type: "teach",
      title: "You left update.exe alone",
      teach: {
        outcome: "safe",
        body:
          "update.exe wasn't a patch, it was **ransomware**. Running it would have encrypted Andrea's files and spread across the bank's network. Leaving it unopened and calling IT on the official number is what keeps one email from stopping a whole bank.",
        spot: [
          "An '.exe' from email is almost never a legitimate update.",
          "A real patch is signed: Windows would not say 'Unknown publisher'.",
          "Real updates come through managed IT tools, not email attachments.",
        ],
        protect: [
          "Never run executables from email, even if a call 'confirmed' them.",
          "Treat an 'Unknown publisher' warning as a stop sign.",
          "Report the email and the call to security.",
        ],
        remember: "Updates don't arrive as email attachments. Don't run them.",
        continueLabel: "See what would have happened",
        whatIf: "If you had run update.exe, this is what it really was.",
      },
    },
    {
      id: "b_ransom",
      type: "device",
      deviceId: "ransom",
      guide: {
        title: "Something's wrong",
        body: "This is Andrea's PC now. Read the note, then click What just happened?",
        actionLabel: "What just happened?",
      },
      whatIfNote: "If you had run update.exe, this is what Andrea's screen would show.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_ransom",
      type: "teach",
      whatIfNote:
        "If you had run update.exe, it would have encrypted every file on Andrea's PC and on the bank's shared drives, deleted the backups, and locked Northbank until it paid 0.85 BTC (about \u20AC58,000).",
      title: "That 'update' was ransomware",
      teach: {
        body:
          "update.exe wasn't a patch \u2014 it was **ransomware**. The moment it ran with Andrea's access, it encrypted her files and spread across the bank's network, then demanded a payment to unlock them. The whole bank's operations stopped because of one file run from one email.",
        spot: [
          "An '.exe' attachment from email is almost never a legitimate update.",
          "The UAC prompt warned 'Unknown publisher' \u2014 a real patch is signed.",
          "Real updates come through managed IT tools, not email attachments.",
        ],
        protect: [
          "Never run executables from email \u2014 even if a call 'confirmed' them.",
          "Treat an 'Unknown publisher' UAC warning as a stop sign.",
          "Report the email/call to security; don't act on it.",
        ],
        remember: "Updates don't arrive as email attachments. Don't run them.",
      },
    },

    { id: "b_to_hacker", type: "camera", waypointId: "wp_hacker_establish", environmentId: "hacker" },
    {
      id: "b_hacker_title",
      type: "title",
      kicker: "The other side",
      title: "Meanwhile, on the attacker's laptop",
      guide: {
        title: "The attacker's view",
        body: "This is the fictional criminal crew behind update.exe. They want to lock Northbank's files and copy them, then demand money to unlock them and keep them private. Here is what they could do after your choice.",
      },
      autoAdvanceMs: 11000,
    },
    { id: "b_hacker_push", type: "camera", waypointId: "wp_hacker_laptop" },
    {
      id: "b_leak",
      type: "device",
      deviceId: "leak",
      guide: {
        title: "The attacker's view",
        body: "The crew didn't just lock the files. Watch what they do next, then click Why this is so damaging.",
        actionLabel: "Why this is so damaging",
      },
      whatIfNote: "If you had run update.exe, this is what the attackers would do next.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_extortion",
      type: "teach",
      whatIfNote:
        "Why this is so damaging: if you had run update.exe, the crew would have copied 412 GB of client data before encrypting anything. Restoring backups would not stop them publishing it.",
      title: "Double extortion",
      teach: {
        body:
          "Before encrypting, the attackers **stole a copy** of the data. Now they threaten to publish it unless the bank pays \u2014 'double extortion'. Even a perfect backup doesn't save you here: the leverage is the leak, not just the lock. This is why preventing the first foothold matters so much.",
        spot: [
          "Modern ransomware steals data first, then encrypts \u2014 so backups alone aren't enough.",
          "The threat is reputational and regulatory, not just operational.",
          "Paying doesn't guarantee deletion \u2014 you're trusting criminals.",
        ],
        protect: [
          "Stop the intrusion early \u2014 verify callers, don't run unknown files.",
          "Report suspected compromise immediately so IT can isolate machines fast.",
          "The faster a foothold is reported, the less data can be exfiltrated.",
        ],
        remember: "Stopping the first click is the cheapest control you have.",
      },
    },

    { id: "b_back_office", type: "camera", waypointId: "wp_office_recut", environmentId: "office" },
    { id: "b_to_tv", type: "camera", waypointId: "wp_tv" },
    {
      id: "b_news",
      type: "device",
      deviceId: "news",
      guide: {
        title: "On the office TV",
        body: "By the afternoon, it's national news. Watch the bulletin, then click Learn more about the threat. Or click Skip the news.",
        actionLabels: ["Learn more about the threat", "Skip the news"],
      },
      completeOn: { action: "continue" },
    },
    {
      // Flows straight on from the bulletin: the anchor speaks a short bridge to
      // camera, then the view zooms in for the data segment (handled in-overlay,
      // so there's no black-TV camera cut between the two beats).
      id: "b_news_explainer",
      type: "device",
      deviceId: "news2",
      guide: {
        title: "The bigger picture",
        body: "The anchor breaks down what just happened and why it keeps happening. Click Next for each part, or Skip the news.",
        actionLabels: ["Next", "Skip the news"],
      },
      completeOn: { action: "continue" },
    },

    {
      id: "b_week_later",
      type: "title",
      title: "Later that week",
      guide: { title: "Later that week", body: "IT has rebuilt Andrea's PC. Security sends everyone a short film." },
      autoAdvanceMs: 5000,
    },
    { id: "b_back_screen", type: "camera", waypointId: "wp_screen" },
    {
      id: "b_desktop_video",
      type: "device",
      deviceId: "desktop",
      title: "Back at your desk",
      guide: {
        title: "Back at your desk",
        body: "Click Edge. The page explains ransomware and has a short film.",
        actionLabel: "Edge",
      },
      completeOn: { action: "open_app", arg: "browser" },
    },
    {
      id: "b_rw_video",
      type: "device",
      deviceId: "rwbrief",
      title: "Watch the film",
      guide: {
        title: "Watch the film",
        body: "Click Watch the video.",
        actionLabel: "Watch the video",
      },
      completeOn: { action: "video_done" },
    },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q1",
          title: "Incident response",
          prompt: "Someone calls saying they're from IT and you must run a security update immediately. What's the safest response?",
          hint: "Verify before you trust.",
          options: [
            { id: "a", label: "Run it \u2014 they gave a ticket number, so it's legitimate", icon: "alert" },
            { id: "b", label: "Hang up and call IT back on the official internal number to verify", correct: true, icon: "phone" },
            { id: "c", label: "Run it only if they also email you the file", icon: "mail" },
          ],
          rationale:
            "Ticket numbers and names can be invented. Verify unexpected IT requests by calling back on a number you look up yourself \u2014 never one the caller provides.",
        },
        {
          id: "q2",
          title: "Email safety",
          prompt: "An email attachment is named 'Security_Update.exe' and a UAC prompt says 'Publisher: Unknown'. You should…",
          hint: "Real updates aren't emailed.",
          options: [
            { id: "a", label: "Click 'Yes, run it' \u2014 IT confirmed it on the phone", icon: "power" },
            { id: "b", label: "Not run it; legitimate updates aren't emailed and are properly signed", correct: true, icon: "shield" },
            { id: "c", label: "Run it but disconnect from Wi-Fi first", icon: "link" },
          ],
          rationale:
            "Real software updates are delivered by managed IT tools and are digitally signed. An '.exe' from email with an 'Unknown publisher' warning is a clear stop sign.",
        },
        {
          id: "q3",
          title: "Ransomware defense",
          prompt: "Why isn't a good backup enough to defeat modern ransomware?",
          hint: "Think about what they take first.",
          options: [
            { id: "a", label: "Backups always get encrypted too, so they're useless", icon: "lock" },
            { id: "b", label: "Attackers steal a copy of the data first and threaten to leak it (double extortion)", correct: true, icon: "file" },
            { id: "c", label: "Backups are too slow to restore in time", icon: "clock" },
          ],
          rationale:
            "Modern crews exfiltrate data before encrypting, then extort you with the threat of publishing it \u2014 so restoring from backup doesn't remove the leverage.",
        },
        {
          id: "q4",
          title: "Prevention",
          prompt: "What single action would most likely have prevented this entire incident?",
          hint: "Where did the whole chain begin?",
          options: [
            { id: "a", label: "Paying the ransom quickly to limit the leak", icon: "x" },
            { id: "b", label: "Verifying the IT caller on the official number before running anything", correct: true, icon: "check" },
            { id: "c", label: "Turning the computer off when the red screen appeared", icon: "power" },
          ],
          rationale:
            "The whole chain started with one unverified call. Verifying the caller \u2014 and never running emailed executables \u2014 stops the attacker before they ever get a foothold.",
        },
      ],
    },

    { id: "b_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "b_intro", to: "b_office_establish" },
    { from: "b_office_establish", to: "b_to_desk" },
    { from: "b_to_desk", to: "b_itcall" },
    { from: "b_itcall", to: "b_teach_it_safe", on: "safe" },
    { from: "b_itcall", to: "b_teach_it" },
    { from: "b_teach_it", to: "b_to_screen" },
    { from: "b_teach_it_safe", to: "b_to_screen" },
    { from: "b_to_screen", to: "b_desktop" },
    { from: "b_desktop", to: "b_mail" },
    { from: "b_mail", to: "b_teach_mail_safe", on: "safe" },
    { from: "b_mail", to: "b_files" },
    { from: "b_teach_mail_safe", to: "b_files" },
    { from: "b_files", to: "b_teach_run_safe", on: "safe" },
    { from: "b_files", to: "b_ransom" },
    { from: "b_teach_run_safe", to: "b_ransom" },
    { from: "b_ransom", to: "b_teach_ransom" },
    { from: "b_teach_ransom", to: "b_to_hacker" },
    { from: "b_to_hacker", to: "b_hacker_title" },
    { from: "b_hacker_title", to: "b_hacker_push" },
    { from: "b_hacker_push", to: "b_leak" },
    { from: "b_leak", to: "b_teach_extortion" },
    { from: "b_teach_extortion", to: "b_back_office" },
    { from: "b_back_office", to: "b_to_tv" },
    { from: "b_to_tv", to: "b_news" },
    { from: "b_news", to: "b_week_later", on: "skip" },
    { from: "b_news", to: "b_news_explainer" },
    { from: "b_news_explainer", to: "b_week_later" },
    { from: "b_week_later", to: "b_back_screen" },
    { from: "b_back_screen", to: "b_desktop_video" },
    { from: "b_desktop_video", to: "b_rw_video" },
    { from: "b_rw_video", to: "b_quiz" },
    { from: "b_quiz", to: "b_debrief" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
